import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import DataTable from "react-data-table-component";
import { LuArrowUpDown } from "react-icons/lu";
import "../assets/css/styles.css";
import Popover from "./popover";
import axiosInstance from "../services/axiosInstance";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { OrderStatusEnum } from "../enums/OrderEnum";
import ViewSheetPopup from "../Popup/viewSheetPopup";
import CheckinPopup from "../Popup/checkinPopup";
import JobSheet from "./jobSheet";
import PreorderSheetForDriver from "./preorderSheetForDriver.jsx";
import { maxWidth } from "@mui/system";
import { PropagateLoader } from "react-spinners";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { useLocation, useNavigate } from "react-router-dom";

const SortIcon = ({ sortDirection }) => {
  if (sortDirection === "asc") {
    return (
      <LuArrowUpDown className="ml-1 !w-4 !h-5" style={{ color: "#EFEFEF" }} />
    );
  }
  if (sortDirection === "desc") {
    return (
      <LuArrowUpDown className="ml-1 !w-4 !h-5" style={{ color: "#EFEFEF" }} />
    );
  }
  return (
    <LuArrowUpDown
      className="ml-1 !w-4 !h-5"
      style={{ color: "#EFEFEF", opacity: "1" }}
    />
  );
};

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#376EA4",
      color: "#FAFDFF",
      fontSize: "16px",
      fontFamily: "InterBold",
      textAlign: "left",
      padding: "15px",
      opacity: "1",
      "@media screen and (min-width: 1250px)niu and (max-width: 1600px)": {
        fontSize: "14px",
      },
    },
    hoverStyle: {
      color: "#FAFDFF",
      opacity: "1",
    },
  },
  rows: {
    style: {
      cursor: "pointer",
      fontSize: "16px",
      color: "#828282",
      padding: "6px",
      backgroundColor: "rgba(252, 252, 252, 0.5)",
      borderBottom: "1px solid rgba(239, 239, 239, 0.8)",
      borderTop: "1px solid rgba(239, 239, 239, 0.8)",
      "@media screen and (min-width: 1250px) and (max-width: 1600px)": {
        fontSize: "14px",
        padding: "4px 8px",
      },
    },
    highlightOnHoverStyle: {
      fontSize: "16px",
      color: "#202020",
      padding: "6px",
      backgroundColor: "#F4F9FF !important",
      borderBottom: "1px solid rgba(239, 239, 239, 0.8)",
      borderTop: "1px solid rgba(239, 239, 239, 0.8)",
      "@media screen and (min-width: 1250px) and (max-width: 1600px)": {
        fontSize: "14px",
        padding: "4px",
      },
    },
  },
  table: {
    style: {
      display: "block",
      maxHeight: "calc(100vh - 330px)",
      height: "fit-content",
      overflowY: "auto",
      borderCollapse: "separate",
      borderSpacing: 0,
      "@media screen and (min-width: 1600px)": {
        maxHeight: "calc(100vh - 340px)",
        overflowX: "hidden",
      },
      "@media screen and (min-width: 760px) and (max-width: 1250px)": {
        maxHeight: "calc(100vh - 330px)",
        overflowX: "hidden",
      },
      "@media screen and (min-width: 768px) and (max-width: 1024px)": {
        maxHeight: "calc(100vh - 360px)",
        overflowX: "hidden",
      },
    },
  },
};

const formatDueDeliveryDate = (dueDeliveryDate) => {
  if (!dueDeliveryDate) return "";

  const date = new Date(dueDeliveryDate);

  // Extract day, month, hours, and minutes
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  // Format as dd.MM | HH:mm
  return `${day}.${month}.${year} | ${hours}:${minutes}`;
};

const DriverOrderTable = ({ searchTerm }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [openCheckinPopup, setOpenCheckinPopup] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [pdfData, setPdfData] = useState([
    {
      index: 0,
      dentistName: "",
      patientName: "",
      dueDeliveryDate: "",
      orderTypeId: 0,
      orderStatusId: 0,
    },
  ]);
  const pdfContainerRef = useRef(null);
  const HandleRemovePopUp = () => setOpenPopup(false);
  const HandleCloseCheckinPopup = () => setOpenCheckinPopup(false);

  const handleEditDriverOrder = (row) => {
    const encryptedId = encrypt(row.id.toString());
    navigate(`/create-driver-order?orderId=${encryptedId}`);
  };

  const columns = [
    {
      name: "Praxis",
      selector: (row) => row.dentistName,
      sortable: true,
      sortFunction: (a, b) => a.dentistName.localeCompare(b.dentistName),
      width: "80%",
    },
    {
      name: "Ausschreiben",
      selector: (row) => row.create,
      sortable: false,
      width: "20%",
      cell: (row) => (
        <Popover
          trigger="hover"
          content={
            <p
              className="absolute right-0 text-blue-400 px-2 py-0.5 3xl:py-1 leading-5 bg-blue rounded w-auto -top-7 text-lg xl:text-xs 3xl:text-lg whitespace-nowrap after:content-[' '] after:-bottom-2 after:right-3  after:absolute after:border after:border-l-8 after:border-r-8 after:border-transparent after:w-0 after:h-0 after:m-auto after:border-t-[10px] after:border-t-blue shadow-[2px_2px_4px_1px_rgba(0,0,0,0.2)] max-w-fit"
              style={{ paddingRight: "3%" }}
            >
              Bearbeiten
            </p>
          }
        >
          <button
            onClick={() => handleEditDriverOrder(row)}
            className="rounded-lg p-1.5 w-10 h-10 my-1 mx-auto border hover:border-blue-600 border-transparent shadow-[0_0_4px_rgba(55,110,164,0.2)]"
            style={{
              backgroundColor: "rgba(201, 226, 252, 0.3)",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            <img src="./icons/edit_square.svg" className="mx-auto" />
          </button>
        </Popover>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const handleRowClick = (row) => {
    setSelectedRows((prevSelectedRows) => {
      const isSelected = prevSelectedRows.some((r) => r.id === row.id);
      if (isSelected) {
        return prevSelectedRows.filter((r) => r.id !== row.id);
      } else {
        return [...prevSelectedRows, row];
      }
    });
  };

  const isRowSelected = (row) => {
    return selectedRows.some((r) => r.id === row.id);
  };

  useEffect(() => {
    fetchUrgentOrders();
  }, []);

  const fetchUrgentOrders = async () => {
    try {
      setLoading(true);
      console.log(OrderStatusEnum.PickedUpAtDentist);
      const response = await axiosInstance.get(
        `/api/order/getPPOrders/${OrderStatusEnum.PickedUpAtDentist}`
      );
      if (response) {
        if (Array.isArray(response)) {
          const mappedOrders = response.map((order) => {
            return {
              id: order.orderId,
              dentistName: order.dentistName || "",
              patientName: order.patientName,
              workTypeName: order.workTypeName,
              orderStatusId: order.orderStatusId,
              orderTypeId: order.orderTypeId,
              finalDelivery: formatDueDeliveryDate(order.deliveryDate),
              finalDeliveryRaw: order.deliveryDate, // Store raw date for sorting
              dueDelivery: formatDueDeliveryDate(order.dueDeliveryDate),
              timeStamp: formatDueDeliveryDate(order.checkoutDate),
              timeStampRaw: order.checkoutDate, // Store raw date for sorting
              isDue:
                new Date(order.deliveryDate) < new Date(order.checkoutDate),
              create: "",
            };
          });

          setOrders(mappedOrders);
          setSelectedRows([]);
        } else {
          setOrders([]);
          setSelectedRows([]);
        }
      } else {
        setOrders([]);
        setSelectedRows([]);
      }
    } catch (error) {
      setOrders([]);
      setSelectedRows([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = React.useMemo(() => {
    const clonedOrders = [...orders];
    return clonedOrders.filter((order) =>
      order.dentistName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [orders, searchTerm]);

  const getTargetElement = (index) =>
    document.getElementById(`pre-order-sheet-for-driver-${index}`);

  const getDueDate = async () => {
    try {
      const payload = [
        {
          departmentId: 1,
          departmentIndex: 0,
          hours: 8,
        },
      ];

      const response = await axiosInstance.post(
        "/api/order/getDueDeliveryDate",
        { AffectedDepartmentIds: payload }
      );
      return response?.data[0]?.dueDeliveryDate; // Assuming the response has a dueDate property
    } catch (error) {
      console.error("Error getting due date:", error);
      throw error;
    }
  };

  const updateDriverOrder = async (selectedOrderIds) => {
    try {
      const response = await axiosInstance.post(
        "/api/Order/updateDriverOrder",
        { OrderIds: selectedOrderIds }
      );

      return response;
    } catch (error) {
      console.error("Error updating driver orders:", error);
      throw error;
    }
  };

  const handlePrintClick = async () => {
    setIsVisible(true);
    const dueDate = await getDueDate();
    const pdfDataArray = selectedRows.map((row, index) => ({
      index: index + 1,
      Orderid: row.id,
      dentistName: row?.dentistName,
      patientName: row.patientName,
      dueDeliveryDate: dueDate,
      orderTypeId: row.orderTypeId,
      orderStatusId: row.orderStatusId,
    }));

    setPdfData(pdfDataArray);

    setTimeout(async () => {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a6",
      });

      for (let index = 0; index < pdfDataArray.length; index++) {
        const element = getTargetElement(index);
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale: 1,
          useCORS: true,
        });
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 148; // A6 width in mm (landscape)
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (index > 0) {
          pdf.addPage();
        }

        const margin = 5;
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          margin,
          imgWidth - 2 * margin,
          imgHeight - 2 * margin
        );
      }
      await updateDriverOrder(selectedRows.map((row) => row.id));
      await fetchUrgentOrders();
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
      // setIsVisible(false);
    }, 100);
  };

  return (
    <div>
      <div>
        {loading ? (
          <div className="flex justify-center items-center min-h-60">
            <PropagateLoader color="rgb(55 110 164 / var(--tw-bg-opacity, 1))" />
          </div>
        ) : (
          <>
            <DataTable
              defaultSortFieldId="finalDeliveryDate"
              defaultSortAsc={true}
              noDataComponent={
                <div className="py-4 text-gray-500">
                  Es sind keine Datensätze zum Anzeigen vorhanden
                </div>
              }
              className=" mt-7 !rounded-xl shadow-[0_0_8px_rgba(44,99,152,0.2)] font-interRegular"
              scrollable
              scrollHeight="600px"
              columns={columns}
              data={filteredOrders}
              pagination={false}
              fixedHeader
              customStyles={customStyles}
              sortIcon={<SortIcon />}
              highlightOnHover={false}
              onRowClicked={handleRowClick}
              conditionalRowStyles={[
                {
                  when: (row) => row.isDue,
                  style: {
                    backgroundColor: "#FE725C33",
                    color: "#828282",
                  },
                },
                {
                  when: (row) => isRowSelected(row),
                  style: {
                    backgroundColor: "#cce5ff",
                    color: "#004085",
                  },
                },
              ]}
            />
            {!loading && (
              <div className="flex justify-end items-end gap-2">
                <div className="text-end mt-4">
                  <button
                    className={`bg-blue text-2xl xl:!text-base 3xl:!text-2xl uppercase border ${
                      selectedRows.length > 0
                        ? "border-blue hover:bg-blue-800 hover:border-blue-800 text-white"
                        : "border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed"
                    } font-robotoBold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all ease-in`}
                    type="button"
                    onClick={handlePrintClick}
                    disabled={selectedRows.length === 0}
                  >
                    DRUCKEN
                  </button>
                </div>
                <div className="text-end mt-4">
                  <button
                    className={`bg-blue text-2xl xl:!text-base 3xl:!text-2xl uppercase border "border-blue hover:bg-blue-800 hover:border-blue-800 text-white
                   font-robotoBold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all ease-in`}
                    type="button"
                    onClick={() => {
                      localStorage.setItem("isDriverOrder", "true");
                      navigate("/create-driver-order");
                    }}
                  >
                    NEU
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* modal popup */}
      <ViewSheetPopup openPopUp={openPopup} closePopUp={HandleRemovePopUp} />
      <CheckinPopup
        openPopUp={openCheckinPopup}
        closePopUp={HandleCloseCheckinPopup}
      />
      <div>
        <PreorderSheetForDriver
          pdfData={pdfData}
          formatDueDeliveryDate={formatDueDeliveryDate}
          isVisible={true}
          selectedDoctor={selectedDoctor}
        />
      </div>

      {/* Hidden container for PDF generation */}
      <div
        ref={pdfContainerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          // overflow: "hidden",
          // pointerEvents: "none",
        }}
      ></div>
    </div>
  );
};

export default DriverOrderTable;
